import { useEffect, useRef, useMemo } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import {
  createColumn,
  getForm,
  getRegister,
  formatDateTime,
  createViewAction,
  addSelectItem,
  beautify,
  formatAmount,
  numberFormat,
} from "../../utils/helpers";
import { URL_COUPON_SALES } from "../../utils/urls";
import useGet from "../common/useGet";
import {
  API_DISTRICTS,
  API_STREETS,
  API_CONFIGURABLES,
  API_COUPON_SALES,
  API_PARKINGS,
} from "../../utils/apis";
import { CAR_SIZE, COUPON_STRIP_AMOUNT } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import { IsManager, IsAccountant } from "../../utils/permissions";

export const useCouponsSales = () => {
  const isManager = IsManager();
  const isAccountant = IsAccountant();

  const {
    loadData: loadCities,
    loading: loadingCities,
    data: citiesData,
  } = useGet();

  const {
    loadData: loadStreets,
    loading: loadingStreets,
    data: streetsData,
  } = useGet();

  useEffect(() => {
    loadCities({ api: API_DISTRICTS });
    loadStreets({ api: API_STREETS });
  }, []);

  const tableColumns = () => [
    createColumn("number_plate", true, "Number Plate"),
    createColumn("created_by", true, "Agent Name"),
    createColumn("parking_name", true, "Yard Name"),
    createColumn("phone_number", true, "Phone Number"),
    createColumn("city_name", true, "City"),
    createColumn("street_name", true, "Street"),
    createColumn("number_of_coupon_strips", false, "Coupon Strips"),
    createColumn("amount", false, "Total Amount"),
    createColumn("payment_mode", false, "Payment Mode"),
    createColumn("created_at", true, "Created Date"),
    createColumn("actions", false, "Actions"),
  ];

  // Get totals by summing the amount column
  const tableTotals = (data: any) => {
    const results = data?.results || data;
    if (!results || !Array.isArray(results)) return null;

    const totalAmountPaid = results?.reduce(
      (sum: number, item: any) => sum + parseFloat(item.amount || 0),
      0,
    );

    return {
      amount: formatAmount(totalAmountPaid),
    };
  };
  const tableData = (data: any) => {
    if (!data || !Array.isArray(data)) return [];

    return data.map((item: any) => ({
      ...item,
      payment_mode: beautify(item?.payment_mode) || "N/A",
      amount: numberFormat(item?.amount),
      created_at: item.created_at ? item.created_at.split("T")[0] : null,
      car_size_name: beautify(item.car_size_name),
      actions: (
        <FormActions actions={[createViewAction(URL_COUPON_SALES, item?.id)]} />
      ),
    }));
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Vehicle Information",
          fields: {
            number_plate: item.number_plate || "N/A",
            car_size_name: beautify(item.car_size_name) || "Unknown Size",
            phone_number: item.phone_number || "N/A",
          },
        },
        {
          title: "Location Details",
          fields: {
            city_name: item.city_name || "Unknown City",
            street_name: item.street_name || "Unknown Street",
          },
        },
        {
          title: "Purchase Details",
          fields: {
            number_of_coupon_strips: item.number_of_coupon_strips || 0,
            coupon_strip_amount: item.coupon_strip_amount
              ? `UGX ${parseFloat(item.coupon_strip_amount).toLocaleString()}`
              : "N/A",
            amount: item.amount
              ? `UGX ${parseFloat(item.amount).toLocaleString()}`
              : "N/A",
            payment_mode: beautify(item.payment_mode) || "N/A",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item.id,
            created_at: formatDateTime(item.created_at),
            updated_at: formatDateTime(item.updated_at),
            created_by: item.created_by || "System",
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_COUPON_SALES);
  const activityFilter = "coupon_sales";

  return {
    tableData,
    tableTotals,
    tableColumns,
    viewData,
    activityFilter,
    citiesData,
    streetsData,

    formRoute: isManager || isAccountant ? null : formRoute,
    loadingCities,
    loadingStreets,
  };
};

export const useCouponsSalesForm = ({
  id,
  form,
  updateFormValues,
}: TFormHookParams = {}) => {
  const prevValuesRef = useRef({
    city: null,
    street: null,
  });

  const {
    loadData: loadCities,
    loading: loadingCities,
    data: cities,
  } = useGet();

  const {
    loadData: loadStreetsUnderACity,
    loading: loadingStreets,
    data: streetsUnderCityData,
  } = useGet();

  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizesData,
  } = useGet();

  const {
    loadData: loadCouponStripAmount,
    loading: loadingCouponStripAmount,
    data: couponStripAmountData,
  } = useGet();

  const {
    loadData: loadParkingsUnderStreet,
    loading: loadingParkingsUnderStreet,
    data: parkingsUnderStreet,
  } = useGet();

  const { city, street, number_of_coupon_strips, payment_mode } = form || {};

  // Get coupon strip amount from configurables
  const couponStripAmount = couponStripAmountData?.results?.[0]?.name
    ? parseFloat(couponStripAmountData.results[0].name)
    : 0;

  const totalAmount = useMemo(() => {
    return number_of_coupon_strips && couponStripAmount
      ? number_of_coupon_strips * couponStripAmount
      : 0;
  }, [number_of_coupon_strips, couponStripAmount]);

  // Load initial data
  useEffect(() => {
    loadCities({ api: API_DISTRICTS });
    loadCarSizes({
      api: API_CONFIGURABLES,
      params: {
        key: CAR_SIZE,
      },
    });
    loadCouponStripAmount({
      api: API_CONFIGURABLES,
      params: {
        key: COUPON_STRIP_AMOUNT,
      },
    });
  }, []);

  // Load streets when city changes
  useEffect(() => {
    if (city && city !== prevValuesRef.current.city) {
      prevValuesRef.current.city = city;
      loadStreetsUnderACity({ api: API_STREETS, params: { district: city } });
    }
  }, [city]);

  useEffect(() => {
    if (street) {
      loadParkingsUnderStreet({
        api: API_PARKINGS,
        params: { street: street },
      });
    }
  }, [street]);

  // Update total amount when number_of_coupon_strips or couponStripAmount changes
  useEffect(() => {
    if (updateFormValues && couponStripAmount) {
      updateFormValues({
        total_amount: totalAmount,
      });
    }
  }, []);

  const isEdit = !!id;

  const fields: any = useMemo(
    () => [
      {
        field_name: "number_plate",
        label: "Number Plate",
        type: "text",
        props: {
          required: true,
          placeholder: "Enter vehicle number plate (e.g., UGA 123S)",
        },
      },
      {
        field_name: "car_size",
        label: "Car Size",
        type: "select",
        props: { required: true },
        options_data: carSizesData?.results,
        loading: loadingCarSizes,
      },

      {
        field_name: "city",
        label: "City",
        type: "select",
        props: { required: true },
        options_data: cities,
        loading: loadingCities,
      },
      {
        field_name: "street",
        label: "Street",
        type: "select",
        props: { required: true },
        options_data: streetsUnderCityData,
        loading: loadingStreets,
      },

      {
        field_name: "parking",
        label: "Parking Yard",
        type: "select",
        props: { required: true },
        options_data: parkingsUnderStreet?.results,
        loading: loadingParkingsUnderStreet,
      },

      {
        field_name: "number_of_coupon_strips",
        label: "Number of Coupon Strips",
        type: "number",
        props: {
          required: true,
          min: 1,
          placeholder: "Enter number of coupon strips",
        },
      },
      {
        field_name: "coupon_strip_amount",
        label: "Amount Per Coupon Strip",
        type: "number",
        props: {
          disabled: true,
          value: couponStripAmount,
          placeholder: "Auto-filled",
        },
        loading: loadingCouponStripAmount,
      },
      {
        field_name: "total_amount",
        label: "Total Amount",
        type: "number",
        props: {
          disabled: true,
          value: totalAmount.toFixed(2),
          placeholder: "Auto-calculated",
        },
      },
      {
        field_name: "payment_mode",
        label: "Payment Mode",
        type: "select",
        props: { required: true },
        options_data: [
          addSelectItem({ label: "Cash", value: "cash" }),
          addSelectItem({ label: "Mobile Money", value: "mobile_money" }),
        ],
      },

      {
        field_name: "phone_number",
        label: "Phone Number",
        type: "tel",
        visible: payment_mode === "mobile_money",
        props: {
          required: payment_mode === "mobile_money",
          placeholder: "Enter phone number (e.g., 256771234567)",
        },
      },
    ],
    [
      carSizesData,
      loadingCarSizes,
      payment_mode,
      cities,
      loadingCities,
      streetsUnderCityData,
      parkingsUnderStreet,
      loadingStreets,
      couponStripAmount,
      loadingCouponStripAmount,
      totalAmount,
    ],
  );

  const sections: IFormSection[] = useMemo(
    () => [
      {
        title: "Vehicle Information",
        fields: fields.slice(0, 5),
      },
      {
        title: "Location Details",
        fields: fields.slice(5, 7),
      },
      {
        title: "Purchase Details",
        fields: fields.slice(8),
      },
    ],
    [fields],
  );

  const title = isEdit ? "Edit Coupon Sale" : "Create New Coupon Sale"; // Made dynamic based on edit mode
  const redirect = getRegister(URL_COUPON_SALES);

  const defaultValues = {
    number_of_coupon_strips: 1,
  };

  const onSubmit = (payload: any, currentId?: string) => {
    // Transform the payload to match API expectations
    const transformedPayload = {
      ...payload,
      car_size: payload.car_size,
      city: payload.city,
      street: payload.street,
    };

    // Remove phone_number if payment mode is not mobile money
    if (payload.payment_mode !== "mobile_money") {
      delete transformedPayload.phone_number;
    }

    if (currentId || id) {
      // Update existing coupon sale
      return {
        id: currentId || id,
        api: API_COUPON_SALES,
        params: transformedPayload,
      };
    }

    // Create new coupon sale
    return {
      api: API_COUPON_SALES,
      params: transformedPayload,
    };
  };

  return {
    fields,
    sections,
    title,
    redirect,
    defaultValues,
    couponStripAmount,
    totalAmount,
    loadingCouponStripAmount,
    loading:
      loadingCities ||
      loadingStreets ||
      loadingCarSizes ||
      loadingCouponStripAmount,
    onSubmit,
  };
};

export const CouponsSalesForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { fields } = useCouponsSalesForm({ form });
  return (
    <FormFields
      fields={fields}
      error={error}
      form={form}
      updateFormValue={updateFormValue}
      onChange={onChange}
    />
  );
};
